/* Admin · QI Claims — Mission Control for damage / shortage / refund claims
 * filed by buyers against sellers. Lists every claim platform-wide with
 * status counters, search, and a click-to-open drawer that surfaces the
 * full event log + Allianz cross-reference.
 *
 * Data source: GET /api/admin/claims (paged list + summary aggregate).
 */
import { useEffect, useMemo, useState } from "react";
import api, { money } from "../../lib/api";
import { PageHeader } from "../../components/PortalShell";
import { Input } from "../../components/ui/input";
import { Sheet, SheetContent, SheetCloseX, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import AdminClaimReply from "../../components/AdminClaimReply";
import {
  Search, AlertTriangle, Filter as FilterIcon, ShieldCheck, ShieldAlert,
  Loader2, ScrollText, Hash, ExternalLink, Package, Receipt,
} from "lucide-react";
import { Link } from "react-router-dom";

const STATUS_TONE = {
  open:                "bg-amber-50 text-amber-700 border-amber-200",
  opened:              "bg-amber-50 text-amber-700 border-amber-200",
  seller_replied:      "bg-indigo-50 text-indigo-700 border-indigo-200",
  in_review:           "bg-indigo-50 text-indigo-700 border-indigo-200",
  counter_offered:     "bg-indigo-50 text-indigo-700 border-indigo-200",
  awaiting_buyer:      "bg-indigo-50 text-indigo-700 border-indigo-200",
  awaiting_seller:     "bg-indigo-50 text-indigo-700 border-indigo-200",
  auto_forwarded_allianz: "bg-sky-50 text-sky-700 border-sky-200",
  settled:             "bg-emerald-50 text-emerald-700 border-emerald-200",
  resolved:            "bg-emerald-50 text-emerald-700 border-emerald-200",
  paid:                "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed:              "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected:            "bg-rose-50 text-rose-700 border-rose-200",
  denied:              "bg-rose-50 text-rose-700 border-rose-200",
  auto_closed_no_proof:"bg-rose-50 text-rose-700 border-rose-200",
  auto_closed:         "bg-rose-50 text-rose-700 border-rose-200",
};

const REASON_LABEL = {
  damaged:             "Damaged in transit",
  shortage:            "Short quantity",
  qty_mismatch:        "Quantity mismatch",
  defect:              "Defective product",
  wrong_item:          "Wrong item shipped",
  not_received:        "Not received",
  late:                "Late delivery",
  other:               "Other",
};

const FILTERS = [
  { v: "",                     l: "All claims" },
  { v: "open",                 l: "Open" },
  { v: "seller_replied",       l: "Seller replied" },
  { v: "auto_forwarded_allianz", l: "Forwarded to Allianz" },
  { v: "settled",              l: "Settled" },
  { v: "auto_closed_no_proof", l: "Auto-closed (no proof)" },
];

function fmtDate(iso, withTime = false) {
  if (!iso) return "—";
  try {
    return withTime ? new Date(iso).toLocaleString() : new Date(iso).toLocaleDateString();
  } catch { return iso; }
}

// Some claims store `current_offer` as an object ({amount, kind}) instead of
// a plain number. Coerce defensively so the table never renders "$NaN".
function amt(v) {
  if (v === null || v === undefined) return null;
  if (typeof v === "object") return v.amount ?? v.value ?? null;
  return v;
}

export default function AdminClaims() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [drawer, setDrawer] = useState(null);

  const load = () => {
    setLoading(true);
    const params = status ? { status } : {};
    api.get("/admin/claims", { params })
      .then(r => { setItems(r.data.items || []); setSummary(r.data.summary || null); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const n = q.trim().toLowerCase();
    return items.filter(c =>
      (c.code || "").toLowerCase().includes(n) ||
      (c.order_code || "").toLowerCase().includes(n) ||
      (c.buyer_name || "").toLowerCase().includes(n) ||
      (c.seller_name || "").toLowerCase().includes(n) ||
      (c.reason || "").toLowerCase().includes(n)
    );
  }, [items, q]);

  return (
    <>
      <PageHeader
        title="QI Claims · Oversight"
        subtitle={summary
          ? `${summary.total} total · ${summary.open} open · ${money(summary.claim_value)} claimed · ${money(summary.approved_value)} approved · click any row for full event log`
          : "Loading…"}
      />

      <div className="p-8 space-y-5" data-testid="admin-claims-page">
        {/* Tiles */}
        <div className="grid md:grid-cols-4 gap-3" data-testid="admin-claims-tiles">
          {[
            { l: "Open / in-review", v: summary?.open ?? "—",       icon: AlertTriangle, tone: "amber" },
            { l: "Settled",          v: summary?.resolved ?? "—",   icon: ShieldCheck,   tone: "emerald" },
            { l: "Auto-closed / denied", v: summary?.denied ?? "—", icon: ShieldAlert,   tone: "rose" },
            { l: "Total claim value", v: summary ? money(summary.claim_value) : "—", icon: Receipt,       tone: "neutral" },
          ].map((t, i) => {
            const Icon = t.icon;
            const toneCls = {
              amber:   "text-amber-700",
              emerald: "text-emerald-700",
              rose:    "text-rose-700",
              neutral: "text-foreground/85",
            }[t.tone];
            return (
              <div key={i} className="rounded-2xl bg-white border border-border p-5" data-testid={`admin-claims-tile-${i}`}>
                <div className="flex items-center gap-2 text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">
                  <Icon className="w-3.5 h-3.5"/>{t.l}
                </div>
                <div className={`display text-2xl mt-1.5 ${toneCls}`}>{t.v}</div>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 justify-between">
          <div className="flex flex-wrap gap-1.5">
            {FILTERS.map(f => {
              const active = status === f.v;
              return (
                <button key={f.v} onClick={() => setStatus(f.v)}
                  data-testid={`admin-claims-filter-${f.v || "all"}`}
                  className={`text-[12px] rounded-full px-3.5 py-1.5 border transition-all ${
                    active
                      ? "bg-foreground text-background border-foreground"
                      : "bg-white border-border text-foreground/65 hover:border-foreground/40"
                  }`}>
                  {f.l}
                </button>
              );
            })}
          </div>
          <div className="relative w-72">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40"/>
            <Input value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search by claim, order, buyer, seller…"
              className="pl-9 h-9 rounded-full bg-white border-border text-[12.5px]"
              data-testid="admin-claims-search"/>
          </div>
        </div>

        {/* Table */}
        <div className="rounded-2xl bg-white border border-border overflow-hidden" data-testid="admin-claims-table">
          {loading ? (
            <div className="p-12 text-center text-foreground/55">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2"/>Loading claims…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-foreground/55">
              <ScrollText className="w-6 h-6 mx-auto mb-2 opacity-40"/>No claims match these filters.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-left text-[11px] uppercase tracking-widest text-foreground/55">
                <tr>
                  <th className="px-5 py-3 font-medium">Claim #</th>
                  <th className="px-5 py-3 font-medium">Order</th>
                  <th className="px-5 py-3 font-medium">Buyer</th>
                  <th className="px-5 py-3 font-medium">Seller</th>
                  <th className="px-5 py-3 font-medium">Reason</th>
                  <th className="px-5 py-3 font-medium text-right">Claimed</th>
                  <th className="px-5 py-3 font-medium text-right">Offered</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Opened</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map(c => (
                  <tr key={c.id} data-testid={`admin-claim-${c.code}`}
                    className="hover:bg-secondary/40 cursor-pointer transition-colors"
                    onClick={() => setDrawer(c)}>
                    <td className="px-5 py-3 mono text-xs">{c.code}</td>
                    <td className="px-5 py-3 mono text-xs">
                      <Link to={`/admin/commerce`} onClick={(e) => e.stopPropagation()}
                        className="text-foreground/75 hover:text-foreground inline-flex items-center gap-1">
                        {c.order_code}<ExternalLink className="w-3 h-3"/>
                      </Link>
                    </td>
                    <td className="px-5 py-3">{c.buyer_name}</td>
                    <td className="px-5 py-3">{c.seller_name}</td>
                    <td className="px-5 py-3">{REASON_LABEL[c.reason] || c.reason}</td>
                    <td className="px-5 py-3 mono text-right">{money(amt(c.requested_amount))}</td>
                    <td className="px-5 py-3 mono text-right">{money(amt(c.current_offer))}</td>
                    <td className="px-5 py-3">
                      <span className={`chip border text-[10.5px] inline-flex items-center gap-1 ${STATUS_TONE[c.status] || "bg-secondary border-border"}`}>
                        {c.status?.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-[11.5px] text-foreground/55 mono">{fmtDate(c.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <ClaimDrawer claim={drawer} onClose={() => setDrawer(null)} onChange={(updated) => {
        // Patch the row in-place + replace the drawer claim so the events log refreshes immediately.
        if (updated) {
          setDrawer(updated);
          setItems((prev) => prev.map(c => c.id === updated.id ? { ...c, ...updated } : c));
        } else {
          // Fallback: reload the list.
          load();
        }
      }}/>
    </>
  );
}

function ClaimDrawer({ claim, onClose, onChange }) {
  if (!claim) return null;
  const events = claim.events || [];
  return (
    <Sheet open={!!claim} onOpenChange={open => !open && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="admin-claim-drawer">
        <SheetCloseX/>
        <SheetHeader>
          <SheetTitle className="display text-2xl tracking-tight flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600"/>{claim.code}
          </SheetTitle>
          <div className="text-[11.5px] text-foreground/55 mono">
            Order {claim.order_code} · {claim.buyer_name} → {claim.seller_name}
          </div>
        </SheetHeader>

        <div className="mt-5 space-y-4">
          {/* Summary */}
          <div className="rounded-xl bg-secondary/40 border border-border p-4 space-y-1.5" data-testid="admin-claim-summary">
            <Kv label="Reason" value={REASON_LABEL[claim.reason] || claim.reason}/>
            <Kv label="Requested outcome" value={claim.requested_outcome}/>
            <Kv label="Requested $" value={money(amt(claim.requested_amount))}/>
            <Kv label="Current offer" value={money(amt(claim.current_offer))}/>
            <Kv label="Resolution" value={claim.resolution}/>
            <Kv label="Status" value={
              <span className={`chip border text-[10.5px] inline-flex items-center gap-1 ${STATUS_TONE[claim.status] || "bg-secondary border-border"}`}>
                {claim.status?.replace(/_/g, " ")}
              </span>
            }/>
            <Kv label="Opened" value={fmtDate(claim.created_at, true)}/>
            <Kv label="Deadline" value={fmtDate(claim.deadline_at, true)}/>
            <Kv label="QI window" value={claim.qi_window_days != null ? `${claim.qi_window_days} days` : "—"}/>
            <Kv label="Score impact" value={claim.score_impact != null ? claim.score_impact : "—"}/>
            {claim.allianz_claim_ref && <Kv label="Allianz claim ref" value={<code className="mono">{claim.allianz_claim_ref}</code>}/>}
          </div>

          {/* Qty mismatch detail (when applicable) */}
          {Array.isArray(claim.qty_mismatch_lines) && claim.qty_mismatch_lines.length > 0 && (
            <div className="rounded-xl border border-border bg-white p-4" data-testid="admin-claim-qty">
              <div className="flex items-center gap-2 mb-2">
                <Package className="w-3.5 h-3.5 text-foreground/55"/>
                <h3 className="text-[12.5px] font-semibold">Quantity mismatch lines</h3>
              </div>
              <ul className="divide-y divide-border/40 text-[12.5px]">
                {claim.qty_mismatch_lines.map((line, i) => (
                  <li key={i} className="py-1.5 flex items-center gap-2">
                    <span className="mono text-foreground/55">{line.sku || `Line ${i + 1}`}</span>
                    <span className="flex-1">{line.name || line.description || "—"}</span>
                    <span className="mono">{line.ordered_qty ?? "—"} → <strong>{line.received_qty ?? "—"}</strong></span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Event log */}
          <div className="rounded-xl border border-border bg-white p-4" data-testid="admin-claim-events">
            <div className="flex items-center gap-2 mb-2">
              <ScrollText className="w-3.5 h-3.5 text-foreground/55"/>
              <h3 className="text-[12.5px] font-semibold">Event log</h3>
              <span className="text-[11px] text-foreground/45 ml-1">{events.length} events</span>
            </div>
            {events.length === 0 ? (
              <div className="text-[12px] text-foreground/55 py-2">No events captured yet.</div>
            ) : (
              <ol className="border-l-2 border-border/40 ml-1 space-y-3">
                {events.map((e, i) => (
                  <li key={e.id || i} className="relative pl-4">
                    <div className={`absolute -left-[7px] top-1 w-3 h-3 rounded-full border ${
                      e.sender_kind === "admin_mediation"
                        ? "bg-indigo-100 border-indigo-300"
                        : "bg-amber-100 border-amber-300"}`}/>
                    <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/45">
                      {fmtDate(e.at, true)}
                      {e.sender_kind === "admin_mediation" && <span className="ml-1 text-indigo-700">· admin mediation</span>}
                    </div>
                    <div className="text-[13px] mt-0.5 font-medium">{e.label || e.status || e.kind || "event"}</div>
                    {e.text && <div className="text-[12px] text-foreground/75 mt-0.5">{e.text}</div>}
                    {e.note && <div className="text-[11.5px] text-foreground/60 mt-0.5">{e.note}</div>}
                    {e.by && <div className="text-[10.5px] text-foreground/40 mt-0.5">by {e.by}</div>}
                  </li>
                ))}
              </ol>
            )}
          </div>

          {/* Admin mediation reply */}
          <AdminClaimReply claim={claim} onChange={onChange}/>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Kv({ label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex items-baseline gap-3">
      <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/50 w-36 shrink-0">{label}</div>
      <div className="text-[13px] text-foreground/85 break-words flex-1">{value}</div>
    </div>
  );
}
